































































import type { MergeKey, MergeValues, Field, Signature } from '@getaccept/editor-lib';
import { Context } from '@getaccept/editor-lib';
import type { PropType } from '@vue/composition-api';
import { ref, watch, computed, defineComponent } from '@vue/composition-api';
import type { MergeTagsSettings } from '@getaccept/editor-lib/src/types/models';
import type { Recipient } from '@getaccept/lib-shared/src/types/Recipient';
import type { User } from '@getaccept/lib-shared/src/users/types/user';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import { SignatureHelper } from '@getaccept/editor-lib/src/helpers/signature.helper';
import type { PartiallySignedEvent } from '@getaccept/editor-lib/src/types/signed-field';
import { useInputFieldView } from '../composables/input-field-view.composable';

export default defineComponent({
  name: 'SignatureItem',
  props: {
    signature: {
      type: Object as PropType<Field>,
      default: () => ({}),
    },
    node: {
      type: Object as PropType<Signature>,
      default: () => ({}),
    },
    mergeValues: { type: Object as PropType<MergeValues>, required: true },
    recipient: { type: Object as PropType<Recipient>, required: true },
    isSigningSite: { type: Boolean, default: false },
    isLoadingSignature: { type: Boolean, default: false },
    sender: { type: Object as PropType<User>, default: () => ({}) },
    focusedRequiredId: { type: String, default: null },
    partiallySignedFields: { type: Array as PropType<PartiallySignedEvent[]>, default: () => [] },
    context: { type: String as PropType<Context>, default: null },
    isPdfViewer: { type: Boolean, default: null },
  },
  setup(props, { emit }) {
    const { getTooltip, getAssignedRecipient, getIsAssignedToCurrentUser } = useInputFieldView(
      props
    );
    const fieldHasFocus = ref(false);

    const fields = computed(() => props.node?.fields || []);
    const signDateSettings = computed(() => props.node?.mergeTagsSettings.signDate);

    const showMergeTagContainer = computed(
      () => getVisibleMergeTags(props.node.mergeTagsSettings).length || signDateSettings.value.show
    );

    const getSignDate = computed(() => {
      if (!signedNode.value) {
        return props.isPdfViewer ? null : t('sign_date').toString();
      }
      return SignatureHelper.formatSignDate(
        signDateSettings.value.format,
        signedNode.value.tsaTime,
        props.mergeValues
      );
    });

    const signedNode = computed(() => {
      return SignatureHelper.hasPartiallySignedField(
        props.partiallySignedFields,
        props.signature.recipientId,
        props.signature.userId,
        props.node.id
      );
    });

    watch(
      () => props.focusedRequiredId,
      () => {
        if (
          getIsAssignedToCurrentUser(props.signature) &&
          props.signature.id === props.focusedRequiredId
        ) {
          fieldHasFocus.value = true;
        } else {
          fieldHasFocus.value = false;
        }
      }
    );

    const getSignatureSvg = computed(() => {
      if (props.signature.userId) {
        return props.context === Context.Document || props.isPdfViewer
          ? props.mergeValues.sender?.signature
          : (props.sender as User).signature;
      }
      if (signedNode.value) {
        emit('update-loading-signature');
        return SignatureHelper.getRecipient(
          props.mergeValues.recipients,
          props.signature.recipientId
        )?.signature;
      }
      return null;
    });

    const getSignatureText = (name: string) => {
      return t('signature_display_here', { name }).toString();
    };

    const getSignatureFieldText = (signature: Field) => {
      if (getIsAssignedToCurrentUser(signature)) {
        return t('add_your_signature_here').toString();
      }
      return getSignatureText(getAssignedRecipient(signature.recipientId)?.fullName);
    };

    const handleClickSignField = (signature: Field) => {
      if (props.isLoadingSignature || getSignatureSvg.value) {
        return;
      }
      if (getIsAssignedToCurrentUser(signature)) {
        emit('open-signature-modal');
      }
    };

    const getVisibleMergeTags = (settings: MergeTagsSettings) => {
      return Object.keys(settings)
        .filter(key =>
          SignatureHelper.isMergeTagEnabled(props.node.mergeTagsSettings, key as MergeKey)
        )
        .map(key => {
          const mergeTag = props.signature.userId
            ? SignatureHelper.getMergeValueForSender(props.mergeValues, key)
            : SignatureHelper.getMergeValueForRecipient(
                props.mergeValues,
                key,
                props.signature.recipientId
              );
          return { key, value: mergeTag };
        });
    };

    return {
      fields,
      getVisibleMergeTags,
      getTooltip,
      getIsAssignedToCurrentUser,
      getAssignedRecipient,
      getSignatureText,
      getSignatureFieldText,
      handleClickSignField,
      getSignatureSvg,
      getSignDate,
      fieldHasFocus,
      signDateSettings,
      showMergeTagContainer,
    };
  },
});
